import { Loader2, RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";
import {
  ApiError,
  type AdminApi,
  type PromptDeployEnvironment,
  type PromptDeploymentPreview,
  type PromptDeploymentResult,
  type PromptDeploymentRow,
  type PromptDeploymentServed,
  type PromptDeploymentStatus,
} from "@/api";
import {
  Badge,
  Banner,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Section,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui";
import { cn } from "@/lib/utils";

const ENV_LABEL: Record<PromptDeployEnvironment, string> = { dev: "Dev", prod: "Prod" };

/** A refusal as SAMS sent it: its message, with its code when it has one. */
function errorText(err: unknown) {
  if (err instanceof ApiError) return err.code ? `${err.message} (${err.code})` : err.message;
  return err instanceof Error ? err.message : String(err);
}

/** Live-Base reads that may fail on their own (e.g. prod has no write role yet); the table still loads without them. */
const TARGET_READ_FAILURES = ["TARGET_NOT_CONFIGURED", "TARGET_UNAVAILABLE"];

type Loaded = { status: PromptDeploymentStatus; verified: PromptDeployEnvironment[]; warning: string };

/**
 * The status with drift checked on every environment this login may read; a
 * target that cannot be read is dropped from `verify` (its error shown above
 * the table) instead of failing the whole table.
 */
async function loadStatus(api: AdminApi, prodAccess: boolean): Promise<Loaded> {
  const attempts: PromptDeployEnvironment[][] = prodAccess ? [["dev", "prod"], ["dev"], []] : [["dev"], []];
  let warning = "";
  for (const verify of attempts) {
    try {
      return { status: await api.promptDeploymentStatus(verify), verified: verify, warning };
    } catch (err) {
      if (!(err instanceof ApiError && TARGET_READ_FAILURES.includes(err.code)) || verify.length === 0) throw err;
      warning ||= `drift 확인 생략 — ${errorText(err)}`;
    }
  }
  throw new Error("unreachable");
}

export function rowLabel(row: { key: string; locale: string; role: string | null }) {
  return `${row.role ? `${row.role} · ` : ""}${row.key}/${row.locale}`;
}

type State = { text: string; tone: "ok" | "pending" | "drift" };

export function rowState(row: PromptDeploymentRow): State {
  const drifted = (["dev", "prod"] as const).filter((e) => row.drift[e] && row.drift[e] !== "unverified");
  if (drifted.length) return { text: `drift (${drifted.join(", ")})`, tone: "drift" };
  if (row.dev_pending) return { text: "dev 대기", tone: "pending" };
  if (row.prod_pending) return { text: "prod 대기", tone: "pending" };
  if (row.catalog_inactive) return { text: "카탈로그 비활성", tone: "pending" };
  return { text: "동기화", tone: "ok" };
}

function Version({ served }: { served: PromptDeploymentServed | null }) {
  if (!served) return <span className="text-muted-foreground">-</span>;
  const title = [`Base v${served.applied_version ?? "-"}`, served.outcome, served.actor, served.recorded_at]
    .filter(Boolean).join(" · ");
  return <span title={title}>v{served.catalog_version ?? "-"}</span>;
}

type Dialogue =
  | { phase: "loading"; environment: PromptDeployEnvironment }
  | { phase: "preview"; environment: PromptDeployEnvironment; preview: PromptDeploymentPreview }
  | { phase: "deploying"; environment: PromptDeployEnvironment; preview: PromptDeploymentPreview }
  | { phase: "done"; environment: PromptDeployEnvironment; preview: PromptDeploymentPreview;
      result: PromptDeploymentResult }
  | { phase: "error"; environment: PromptDeployEnvironment; preview?: PromptDeploymentPreview; error: string };

/**
 * Prompt deployment as one table (catalog / dev / prod per prompt and sub-agent
 * role) and two buttons. Each button previews (`dry_run`), shows what changes,
 * and deploys that exact preview on confirm. Prod is offered only with prod
 * access; the server checks both and its refusal is shown as it came.
 */
export function PromptDeploymentPanel({ api, prodAccess }: { api: AdminApi; prodAccess: boolean }) {
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialogue, setDialogue] = useState<Dialogue | null>(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      setLoaded(await loadStatus(api, prodAccess));
    } catch (err) {
      setError(errorText(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, prodAccess]);

  const open = async (environment: PromptDeployEnvironment) => {
    setDialogue({ phase: "loading", environment });
    try {
      setDialogue({ phase: "preview", environment, preview: await api.previewPromptDeployment(environment) });
    } catch (err) {
      setDialogue({ phase: "error", environment, error: errorText(err) });
    }
  };

  const confirm = async () => {
    if (dialogue?.phase !== "preview") return;
    const { environment, preview } = dialogue;
    setDialogue({ phase: "deploying", environment, preview });
    try {
      const result = await api.deployPrompts(environment, preview.preview_sha256, preview.drift_sha256);
      setDialogue({ phase: "done", environment, preview, result });
    } catch (err) {
      setDialogue({ phase: "error", environment, preview, error: errorText(err) });
    }
    await load();
  };

  const rows = loaded?.status.rows ?? [];
  const unverified = (["dev", "prod"] as const).filter((e) => !loaded?.verified.includes(e));
  const busy = loading || dialogue?.phase === "loading" || dialogue?.phase === "deploying";
  const preview = dialogue && "preview" in dialogue ? dialogue.preview : undefined;

  return (
    <Section title="배포" contentClassName="grid gap-3">
      {error && <Banner tone="error"><span className="wrap-anywhere">{error}</span></Banner>}
      {loaded?.warning && <p className="text-[13px] text-muted-foreground" role="status">{loaded.warning}</p>}
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={() => void open("dev")} disabled={busy}>Dev 배포</Button>
        {prodAccess && <Button size="sm" onClick={() => void open("prod")} disabled={busy}>Prod 배포</Button>}
        <Button variant="outline" size="sm" onClick={() => void load()} disabled={busy}>
          {loading ? <Loader2 className="animate-spin" /> : <RefreshCw />}
          새로고침
        </Button>
        {loaded && (
          <span className="text-[13px] text-muted-foreground">
            dev 대기 {loaded.status.dev_pending} · prod 대기 {loaded.status.prod_pending}
            {unverified.length > 0 && ` · drift 미확인: ${unverified.join(", ")}`}
          </span>
        )}
      </div>
      {/* Fixed layout: long keys are cut to one line (whole value in the tooltip),
          and a narrow screen scrolls inside the table's own container. */}
      <Table aria-label="프롬프트 배포 상태" className="min-w-[560px] table-fixed">
        <colgroup>
          <col />
          <col className="w-[76px]" />
          <col className="w-[76px]" />
          <col className="w-[76px]" />
          <col className="w-[132px]" />
        </colgroup>
        <TableHeader>
          <TableRow>
            <TableHead>프롬프트</TableHead>
            <TableHead>카탈로그</TableHead>
            <TableHead>dev</TableHead>
            <TableHead>prod</TableHead>
            <TableHead>상태</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => {
            const label = rowLabel(row);
            const state = rowState(row);
            return (
              <TableRow key={`${row.subject}:${label}`} className="h-10">
                <TableCell className="overflow-hidden">
                  <span className="block truncate font-mono text-xs" title={label}>{label}</span>
                </TableCell>
                <TableCell className="text-xs tabular-nums">
                  {row.catalog ? `v${row.catalog.version}` : <span className="text-muted-foreground">-</span>}
                </TableCell>
                <TableCell className="text-xs tabular-nums"><Version served={row.dev} /></TableCell>
                <TableCell className="text-xs tabular-nums"><Version served={row.prod} /></TableCell>
                <TableCell className="overflow-hidden">
                  <Badge
                    size="compact"
                    variant={state.tone === "pending" ? "accent" : "default"}
                    className={cn(state.tone === "drift" && "border-destructive-line bg-destructive-surface text-destructive")}
                  >
                    {state.text}
                  </Badge>
                </TableCell>
              </TableRow>
            );
          })}
          {!loading && rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} className="text-muted-foreground">
                {error ? "상태를 불러오지 못했습니다." : "프롬프트가 없습니다."}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Dialog open={dialogue !== null} onOpenChange={(next: boolean) => {
        if (!next && dialogue?.phase !== "deploying") setDialogue(null);
      }}>
        {/* Grid children may shrink (min-w-0) and long keys wrap, so a long
            refusal never pushes past the dialog's edge. */}
        <DialogContent showCloseButton={false} className="sm:max-w-lg [&>*]:min-w-0">
          <DialogTitle>{dialogue ? `${ENV_LABEL[dialogue.environment]} 배포` : ""}</DialogTitle>
          <DialogDescription>
            {dialogue?.phase === "loading" && "바뀌는 내용을 확인하는 중입니다…"}
            {dialogue?.phase === "preview" && (preview!.changed.length
              ? `${preview!.changed.length}건이 바뀝니다. 그대로 ${preview!.unchanged}건.`
              : `바뀌는 항목이 없습니다. 그대로 ${preview!.unchanged}건.`)}
            {dialogue?.phase === "deploying" && "배포하는 중입니다…"}
            {dialogue?.phase === "done" && `결과: ${dialogue.result.state}`}
            {dialogue?.phase === "error" && "서버가 거절했습니다."}
          </DialogDescription>
          {dialogue?.phase === "error" && <Banner tone="error"><span className="wrap-anywhere">{dialogue.error}</span></Banner>}
          {preview && preview.changed.length > 0 && (
            <ul className="grid max-h-64 gap-1 overflow-y-auto text-xs" aria-label="바뀌는 항목">
              {preview.changed.map((change) => (
                <li key={`${change.subject}:${rowLabel(change)}`} className="flex min-w-0 items-center gap-2">
                  <span className="min-w-0 flex-1 truncate font-mono" title={rowLabel(change)}>{rowLabel(change)}</span>
                  <span className="shrink-0 tabular-nums">
                    {change.withdraw ? "철회" : `→ v${change.catalog_version ?? "-"}`}
                  </span>
                  {dialogue?.phase === "done" && (
                    <Badge size="compact" variant="accent" className="shrink-0">{dialogue.result.state}</Badge>
                  )}
                </li>
              ))}
            </ul>
          )}
          {preview && preview.settled_withdrawals.length > 0 && (
            <p className="text-xs text-muted-foreground wrap-anywhere">이미 철회됨(기록만): {preview.settled_withdrawals.join(", ")}</p>
          )}
          {dialogue?.phase === "preview" && preview!.drift.length > 0 && (
            <Banner tone="error">
              <span className="wrap-anywhere">
              {ENV_LABEL[dialogue.environment]} Base가 SAMS 밖에서 바뀌었습니다. 배포하면 덮어씁니다:{" "}
              {preview!.drift.map(rowLabel).join(", ")}
              </span>
            </Banner>
          )}
          {dialogue?.phase === "done" && (
            <p className="text-xs text-muted-foreground">배포 id {dialogue.result.deployment_id}</p>
          )}
          <div className="flex justify-end gap-2">
            {dialogue?.phase === "preview" ? (
              <>
                <Button variant="outline" onClick={() => setDialogue(null)}>취소</Button>
                <Button
                  className={cn(preview!.drift.length > 0 && "bg-destructive text-white hover:bg-destructive/90")}
                  onClick={() => void confirm()}
                >
                  {preview!.drift.length > 0 ? "덮어쓰고 배포" : "배포"}
                </Button>
              </>
            ) : (
              <Button variant="outline" onClick={() => setDialogue(null)}
                disabled={dialogue?.phase === "loading" || dialogue?.phase === "deploying"}>
                {dialogue?.phase === "loading" || dialogue?.phase === "deploying"
                  ? <><Loader2 className="animate-spin" />진행 중</> : "닫기"}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Section>
  );
}
